import type {
  AgentsDemoOrg,
  OrgAgent,
  OrgConnection,
  OrgSchedule,
  OrgSharedInbox,
} from "@/lib/agentsDemoData";
import { migrateOrgAgents } from "@/lib/agentsDemoData";

export type AgentsViewerMode = "super_admin" | "standard";

const STORAGE_VERSION = 4;
/** Legacy browser cache / one-time import source. Server D1 is authoritative. */
export const AGENTS_LOCAL_STORAGE_KEY = "atw_agents_demo_v1";
const STORAGE_KEY = AGENTS_LOCAL_STORAGE_KEY;
const IMPORT_FLAG_PREFIX = "atw_agents_roster_imported_v1:";

// Mailbox fields live on each agent object, and the mail flag lives on each
// connection object. v4 already stores those arrays whole, so older snapshots
// keep hired agents and saved connections (including empty arrays). Missing
// mailbox fields mean null.
// Schedules are a new org list on the same v4 snapshot. A missing list means
// none yet. An empty schedules array stays empty. Assignments live on each
// agent as scheduleIds. Do not bump the version for them.
// Shared inboxes follow the same pattern: optional on older v4 snapshots.
// Personal mail status is migrated in place: magic-click personalMailConnected
// stubs become disconnected unless status is explicitly connected with a mailbox.
// Connected agents still on create+draft defaults upgrade to provider-possible caps.

export type AgentsPersistedSnapshot = {
  v: number;
  agents: OrgAgent[];
  maxAddon: boolean;
  connections: OrgConnection[];
  schedules: OrgSchedule[];
  sharedInboxes: OrgSharedInbox[];
  viewerMode: AgentsViewerMode;
};

function isViewerMode(v: unknown): v is AgentsViewerMode {
  return v === "super_admin" || v === "standard";
}

function isAgentList(value: unknown): value is OrgAgent[] {
  return Array.isArray(value);
}

function readSchedules(value: unknown): OrgSchedule[] {
  return Array.isArray(value) ? (value as OrgSchedule[]) : [];
}

function readSharedInboxes(value: unknown): OrgSharedInbox[] {
  return Array.isArray(value) ? (value as OrgSharedInbox[]) : [];
}

function safeParse(raw: string | null): AgentsPersistedSnapshot | null {
  if (!raw) return null;
  try {
    const j = JSON.parse(raw) as {
      v?: unknown;
      agents?: unknown;
      seats?: unknown;
      maxAddon?: unknown;
      connections?: unknown;
      schedules?: unknown;
      sharedInboxes?: unknown;
      viewerMode?: unknown;
    };
    if (!isViewerMode(j.viewerMode)) return null;
    if (typeof j.maxAddon !== "boolean") return null;
    if (typeof j.v !== "number") return null;

    // v4 keeps hired agents and any connections added in this browser.
    // An empty connections array is a real value. Do not treat it as missing.
    // v1-v3 saved the sample connections. Drop those so they do not come back.
    // Keep the saved agents list so hired agents stay.
    // Schedules and shared inboxes are optional on older v4 snapshots.
    // Missing means none. An empty array stays empty. Never replace with samples.
    let agents: OrgAgent[] | null = null;
    let connections: OrgConnection[] | null = null;
    let schedules: OrgSchedule[] = [];
    let sharedInboxes: OrgSharedInbox[] = [];
    if (j.v === STORAGE_VERSION) {
      if (!isAgentList(j.agents) || !Array.isArray(j.connections)) return null;
      agents = j.agents;
      connections = j.connections as OrgConnection[];
      schedules = readSchedules(j.schedules);
      sharedInboxes = readSharedInboxes(j.sharedInboxes);
    } else if (j.v === 1 || j.v === 2 || j.v === 3) {
      agents = isAgentList(j.agents) ? j.agents : [];
      connections = [];
      schedules = [];
      sharedInboxes = [];
    }
    if (agents === null || connections === null) return null;

    return {
      v: STORAGE_VERSION,
      agents: migrateOrgAgents(agents),
      maxAddon: j.maxAddon,
      connections,
      schedules,
      sharedInboxes,
      viewerMode: j.viewerMode,
    };
  } catch {
    return null;
  }
}

export function loadAgentsPersistedSnapshot(): AgentsPersistedSnapshot | null {
  if (typeof window === "undefined") return null;
  return safeParse(window.localStorage.getItem(STORAGE_KEY));
}

export function mergeOrgWithPersisted(
  base: AgentsDemoOrg,
  snap: AgentsPersistedSnapshot | null,
): AgentsDemoOrg {
  if (!snap) return base;
  return {
    ...base,
    agents: snap.agents,
    maxAddon: snap.maxAddon,
    connections: snap.connections,
    schedules: snap.schedules,
    sharedInboxes: snap.sharedInboxes,
  };
}

export function buildPersistedSnapshot(
  org: AgentsDemoOrg,
  viewerMode: AgentsViewerMode,
): AgentsPersistedSnapshot {
  return {
    v: STORAGE_VERSION,
    agents: org.agents,
    maxAddon: org.maxAddon,
    connections: org.connections,
    schedules: org.schedules,
    sharedInboxes: org.sharedInboxes,
    viewerMode,
  };
}

export function saveAgentsPersistedSnapshot(snap: AgentsPersistedSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    /* quota or private mode */
  }
}

export function clearAgentsPersistedSnapshot(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function importFlagKey(orgId: string): string {
  return `${IMPORT_FLAG_PREFIX}${orgId.trim()}`;
}

/** True when this browser already attempted a one-time local→server import for the org. */
export function hasAttemptedRosterImport(orgId: string): boolean {
  if (typeof window === "undefined") return true;
  const id = orgId.trim();
  if (!id) return true;
  try {
    return window.localStorage.getItem(importFlagKey(id)) === "1";
  } catch {
    return true;
  }
}

export function markRosterImportAttempted(orgId: string): void {
  if (typeof window === "undefined") return;
  const id = orgId.trim();
  if (!id) return;
  try {
    window.localStorage.setItem(importFlagKey(id), "1");
  } catch {
    /* ignore */
  }
}

export function localSnapshotHasHireData(snap: AgentsPersistedSnapshot | null): boolean {
  if (!snap) return false;
  return (
    snap.agents.length > 0 ||
    snap.connections.length > 0 ||
    snap.schedules.length > 0 ||
    snap.sharedInboxes.length > 0
  );
}

export function snapshotToRosterWrite(snap: AgentsPersistedSnapshot) {
  return {
    agents: snap.agents,
    connections: snap.connections,
    schedules: snap.schedules,
    sharedInboxes: snap.sharedInboxes,
    maxAddon: snap.maxAddon,
  };
}
